import React, { useState } from "react";
import Background from "../Background";
import {
  SimpleGrid,
  Box,
  Flex,
  Text,
  Avatar,
  Button,
  Icon,
  Switch,
  Spinner,
  useToast,
  HStack,
  Image as Images,
} from "@chakra-ui/react";
import { COLORS } from "../../Helper/Colors";
import ProfileImage from "../../Assets/profile_placeholder.jpg";
import { MiniStatistics } from "./MiniStatistics";
import {
  Globe,
  BookBookmark,
  Image,
  Cards,
  Trash,
  CaretDoubleRight,
} from "phosphor-react";
import LineChart from "../../Components/Common/Charts/LineCharts";
import BarCharts from "../../Components/Common/Charts/BarCharts";
import TableStyle from "../../Components/Common/TableStyle";
import TableLoader from "../../Components/Common/TableLoader";
import { DeleteModal, Pagination, Search } from "../../Components/Common/UI";
import TableV6 from "../../Components/Common/TableV6";
import Api from "../../Helper/ApiHandler";
import { useQuery } from "react-query";
import { useDebounce } from "../../Helper/Hooks/useDebounce";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";

const MainDashboard = () => {
  // eslint-disable-next-line no-unused-vars
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const api = new Api();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [params, setParams] = useState({
    pageNo: 1,
    pageSize: 25,
    order: "DESC",
    sortBy: "id",
  });
  const [changeId, setChangeId] = useState(null);
  const [deleteBtnLoading, setDeleteBtnLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [cardData, setCardData] = useState([]);
  const [userStartDate, setUserStartDate] = useState(new Date());
  const [postStartDate, setPostStartDate] = useState(new Date());

  const debouncedSearchTerm = useDebounce(search, 1000);
  let addDefaultSrc = (ev) => {
    ev.target.src = ProfileImage;
  };

  const fetchUser = async ({ queryKey }) => {
    setIsCartLoading(true);
    const cardData1 = await api.post(`${"dashboard/card"}`);
    setCardData(cardData1);
    setIsCartLoading(false);

    const userList = await api.post(`${"dashboard/list-user"}`, {
      page: state?.count ? state.pageNo : queryKey[2].pageNo,
      search: state?.count ? state.search : queryKey[1],
    });
    setData(userList?.data?.rows);
    return userList;
  };

  const {
    isLoading,
    data: userData,
    refetch,
  } = useQuery(["userLists", debouncedSearchTerm, params], fetchUser);

  const handleSwitchChange = async (value) => {
    try {
      setChangeId(value._id);
      await api.put("user/change-status", {
        userId: value._id,
      });
      await refetch();
      setChangeId(null);
      return;
    } catch (error) {
      toast({
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    }
  };

  const handleSortBy = (sortBy) => {
    setParams((prev) => ({
      ...prev,
      sortBy: sortBy[0]?.id || "id",
      order: sortBy[0]?.desc ? "DESC" : "ASC",
    }));
  };

  const handleDelete = async (type) => {
    if (type === "CANCEL") {
      setIsDeleteModalOpen(false);
      return;
    }
    try {
      setDeleteBtnLoading(true);
      await api.put("user/delete", { userId: String(deleteId) });
      setIsDeleteModalOpen(false);
      setDeleteBtnLoading(false);
      setDeleteId(null);
      toast({
        description: "User has been deleted.",
        status: "success",
        duration: 3000,
        position: "top",
      });

      if (params.pageNo > 1 && userData?.data?.length === 1) {
        setParams({ ...params, pageNo: params.pageNo - 1 });
      } else {
        await refetch();
      }
    } catch (error) {
      toast({
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
      setDeleteBtnLoading(false);
    }
  };

  const columns = [
    {
      Header: () => (
        <Box>
          <Text>Image</Text>
        </Box>
      ),
      accessor: "profilePhotoKey",
      Cell: (row) => (
        <Box display={"flex"} justifyContent={"center"}>
          <Images
            onError={addDefaultSrc}
            borderRadius="full"
            objectFit={"contain"}
            boxSize="55px"
            src={row.value == null ? ProfileImage : row.value}
            alt="Img"
          />
        </Box>
      ),
      sortable: false,
    },
    {
      Header: () => (
        <Box>
          <Text marginLeft={"5px"}>Name</Text>
        </Box>
      ),
      accessor: "firstName",
      Cell: (row) => (
        <Text textAlign={"center"}>
          {row.original.firstName ?? "" + " " + row.original.lastName ?? ""}
        </Text>
      ),
      sortable: false,
    },
    {
      Header: () => (
        <Box>
          <Text marginLeft={"5px"}>UserName</Text>
        </Box>
      ),
      accessor: "userName",
      Cell: (row) => <Text textAlign={"center"}>{row.original.userName}</Text>,
      sortable: false,
    },
    {
      Header: () => (
        <Box>
          <Text marginLeft={"5px"}>Email</Text>
        </Box>
      ),
      accessor: "email",
      Cell: (row) => (
        <Text textAlign={"center"} textTransform={"lowercase"}>
          {row.value}
        </Text>
      ),
      sortable: false,
    },
    {
      Header: () => (
        <Text textAlign={"center"} marginLeft={"15px"}>
          Status
        </Text>
      ),
      accessor: "isActive",
      Cell: (row) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {changeId !== row.original._id ? (
            <Switch
              isChecked={row.value}
              onChange={() => handleSwitchChange(row.original)}
            />
          ) : (
            <Spinner color={COLORS.brand} size={"sm"} />
          )}
        </div>
      ),
      sortable: false,
      width: 100,
    },
    {
      Header: () => (
        <Box>
          <Text borderColor={"white"} boxShadow="none" marginLeft={"10px"}>
            Action
          </Text>
        </Box>
      ),
      accessor: "_id",
      Cell: (row) => (
        <>
          <HStack
            alignItems={"center"}
            justifyContent="center"
            textAlign={"center"}
          >
            <Button
              backgroundColor={COLORS.brand}
              color={"white"}
              title="Delete"
              _hover={{ backgroundColor: COLORS.lightPink }}
              onClick={() => {
                setDeleteId(row.original._id);
                setIsDeleteModalOpen(true);
              }}
            >
              {<Trash size={18} color={"white"} />}
            </Button>

            <Button
              backgroundColor={COLORS.brand}
              color={"white"}
              title="View Profile"
              transition="all .8s ease"
              mx=".3rem"
              cursor="pointer"
              _hover={{ transform: "translate(10%)" }}
              // _hover={{ backgroundColor: COLORS.brand }}
              onClick={() => {
                navigate(`/user/${row.value}`, {
                  state: {
                    ...params,
                    search: search,
                  },
                });
              }}
            >
              <Icon
                as={CaretDoubleRight}
                w="20px"
                h="20px"
                fontSize="xl"
                pt="4px"
              />
              {/* {<CaretDoubleRight size={18} color={"white"} />} */}
            </Button>
          </HStack>
        </>
      ),
      sortable: false,
    },
  ];

  return (
    <>
      <Background heading={"Dashboard"} key="dashboard">
        <Box overflow={"scroll"} className="design-scrollbar">
          <Box flex={1}>
            <SimpleGrid width={"100%"}>
              <SimpleGrid
                columns={{ sm: 1, md: 2, xl: 4 }}
                position={"relative"}
              >
                <Box width={"90%"}>
                  <MiniStatistics
                    title={"Total Users"}
                    amount={cardData?.data?.userData?.totalCount}
                    percentage={
                      cardData?.data?.userData?.increase[0]?.user ?? "0"
                    }
                    icon={
                      <Globe height={"24px"} width={"24px"} color={"white"} />
                    }
                  />
                </Box>
                <Box width={"90%"}>
                  <MiniStatistics
                    title={"Total PhtoBooks"}
                    amount={cardData?.data?.photoBookData?.totalCount}
                    percentage={
                      cardData?.data?.photoBookData?.increase[0]?.photobook ??
                      "0"
                    }
                    icon={
                      <BookBookmark
                        height={"24px"}
                        width={"24px"}
                        color={"white"}
                      />
                    }
                  />
                </Box>
                <Box width={"90%"}>
                  <MiniStatistics
                    title={"Total Albums"}
                    amount={cardData?.data?.albumData?.totalCount}
                    percentage={
                      cardData?.data?.albumData?.increase[0]?.album ?? "0"
                    }
                    icon={
                      <Cards height={"24px"} width={"24px"} color={"white"} />
                    }
                  />
                </Box>
                <Box width={"90%"}>
                  <MiniStatistics
                    title={"Total Posts"}
                    amount={cardData?.data?.postData?.totolCount}
                    percentage={
                      cardData?.data?.postData?.increase[0]?.post ?? "0"
                    }
                    icon={
                      <Image height={"24px"} width={"24px"} color={"white"} />
                    }
                  />
                </Box>
              </SimpleGrid>
            </SimpleGrid>
          </Box>
          <Box display={"flex"} flex={1} flexDirection={"column"}>
            <TableLoader isLoading={isCartLoading} version={6} />
            <SimpleGrid columns={{ sm: 1, md: 2, xl: 2 }} width={"100%"}>
              <Box
                backgroundColor={"white"}
                height="450px"
                padding={"30px"}
                width={"95%"}
                borderRadius={"15px"}
                marginLeft={"2"}
              >
                <Flex
                  direction="row"
                  alignItems="center"
                  justifyContent={"space-between"}
                >
                  <Text fontSize="lg" fontWeight="bold" mb="6px">
                    New Users
                  </Text>
                  <Box width="200px">
                    <DatePicker
                      showIcon
                      selected={userStartDate}
                      onChange={(date) => setUserStartDate(date)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select start date"
                      className="react-datepicker-input"
                      wrapperClassName="react-datepicker-wrapper"
                    />
                  </Box>
                </Flex>
                <BarCharts selectedDate={userStartDate} />
              </Box>

              <Box
                backgroundColor={"white"}
                borderRadius={"15px"}
                width={"95%"}
                marginLeft={"1.5"}
              >
                <Box p="28px 10px 16px 0px" mb={{ sm: "26px", lg: "0px" }}>
                  <Box mb="20px" pl="22px">
                    <Flex
                      direction="row"
                      alignItems="center"
                      justifyContent={"space-between"}
                    >
                      <Text fontSize="lg" fontWeight="bold" mb="6px">
                        New Posts
                      </Text>
                      <Box width="200px" mr={"13px"}>
                        <DatePicker
                          showIcon
                          selected={postStartDate}
                          onChange={(date) => setPostStartDate(date)}
                          dateFormat="yyyy-MM-dd"
                          placeholderText="Select start date"
                          className="react-datepicker-input"
                          wrapperClassName="react-datepicker-wrapper"
                        />
                      </Box>
                    </Flex>
                  </Box>
                  <Box w="100%" h={{ sm: "300px" }} ps="8px">
                    <LineChart selectedDate={postStartDate} />
                  </Box>
                </Box>
              </Box>
            </SimpleGrid>
          </Box>

          <Box marginTop={"5"} position={"relative"}>
            <Text fontSize="lg" fontWeight="bold" mb="6px" ml="2">
              Recently Joined Users
            </Text>
            <Box>
              <TableStyle version={6}>
                <TableV6
                  columns={columns}
                  data={data}
                  handleSortBy={handleSortBy}
                  isLoading={isLoading}
                  key={"master-rep-table"}
                />
              </TableStyle>
            </Box>
            <DeleteModal
              heading={"Delete User"}
              isOpen={isDeleteModalOpen}
              onCancel={() => handleDelete("CANCEL")}
              onDelete={() => handleDelete("DELETE")}
              deleteBtnLoading={deleteBtnLoading}
            />
          </Box>
        </Box>
      </Background>
    </>
  );
};

export default MainDashboard;
